import * as Yup from 'yup';
import { mapValues } from 'lodash';
import { remunerationRules, getParsedRemunerationEps } from './helpers';
import { RemunerationTypeFields } from './interfaces';
import { IPossiblyDraftSelectedEPS } from 'interfaces/MailingRule';
import { addDays } from 'date-fns';

const REQUIRED_MESSAGE = 'Campo obrigatório';
const MIN_MESSAGE = 'Selecione uma opção';

const testRangeFieldOption = ({
  optional = false,
  allowMinZero = false,
} = {}) => ({
  name: 'rangeField',
  message: 'O campo mínimo deve ser maior que zero e menor que o campo máximo',
  test: ({ min = 0, max = 0 }) => {
    const minIsValid = allowMinZero ? min >= 0 : min > 0;
    return (optional && min === 0 && max === 0) || (minIsValid && min < max);
  },
});

const rangeFieldSchemaOptional = Yup.object({
  min: Yup.number(),
  max: Yup.number(),
}).test(testRangeFieldOption({ optional: true }));

const rangeFieldSchema = Yup.object()
  .shape({
    min: Yup.number().required('O campo mínimo é obrigatório'),
    max: Yup.number().required('O campo máximo é obrigatório'),
  })
  .test(testRangeFieldOption());

const rangeFieldSchemaAllowMinZero = Yup.object()
  .shape({
    min: Yup.number().min(-1).required('O campo mínimo é obrigatório'),
    max: Yup.number().required('O campo máximo é obrigatório'),
  })
  .test(testRangeFieldOption({ allowMinZero: true }));

const selectedEPSSchema = Yup.lazy((obj) =>
  Yup.object(
    mapValues(obj, () =>
      Yup.object({
        companyName: Yup.string().required(REQUIRED_MESSAGE),
        distributionPercentage: Yup.number().required(REQUIRED_MESSAGE),
        remunerationType: Yup.string().default('EXEMPTED'),
        remunerationCommissionId: Yup.string().when('remunerationType', {
          is: 'COMMISSION',
          then: Yup.string().required(REQUIRED_MESSAGE),
          otherwise: Yup.string().optional(),
        }),
        remunerationBonusId: Yup.string().when('remunerationType', {
          is: 'EXEMPTED',
          then: Yup.string().optional(),
          otherwise: Yup.string().required(REQUIRED_MESSAGE),
        }),
        remunerationGoalIds: Yup.array().when('remunerationType', {
          is: 'EXEMPTED',
          then: Yup.array().optional(),
          otherwise: Yup.array()
            .of(Yup.string())
            .min(1, REQUIRED_MESSAGE)
            .required(),
        }),
        remunerationFixedIds: Yup.array().when('remunerationType', {
          is: 'VALUE_FIXED',
          then: Yup.array()
            .of(Yup.string())
            .min(1, REQUIRED_MESSAGE)
            .required(),
          otherwise: Yup.array().optional(),
        }),
      })
        .test(
          'eps remuneration',
          'As EPSs selecionadas devem ser configuradas corretamente',
          (remunerationEps, testContext) => {
            const parsedRemunerationEps = getParsedRemunerationEps(
              remunerationEps as IPossiblyDraftSelectedEPS,
            );
            const isValid: boolean = remunerationEps.remunerationType
              ? remunerationRules[remunerationEps.remunerationType].every(
                  (fieldName: RemunerationTypeFields) =>
                    parsedRemunerationEps[fieldName],
                )
              : false;

            return (
              isValid ||
              testContext.createError({
                path: 'selectedEPS_Remuneration',
              })
            );
          },
        )
        .required(REQUIRED_MESSAGE),
    ),
  )
    .required(REQUIRED_MESSAGE)
    .test(
      'notEmpty',
      'Selecione ao menos uma EPS',
      (value, testContext) =>
        !!Object.keys(value).length ||
        testContext.createError({ path: 'selectedEPS_Root' }),
    )
    .test(
      'distribution',
      'As distribuições selecionadas para o Tipo de Atuação Ação Ativa/ESCOB devem somar 100%',
      (value, testContext) => {
        const selectedEPSValues = Object.values(value);
        if (!selectedEPSValues.length) return true;

        const sum = selectedEPSValues.reduce(
          (sum, current) =>
            current.distributionPercentage
              ? sum + current.distributionPercentage
              : sum,
          0,
        );

        if (
          testContext?.parent?.serviceCategory !== '6010a536b8dde377826544c1'
        ) {
          return true;
        }

        return (
          sum === 100 || testContext.createError({ path: 'selectedEPS_Root' })
        );
      },
    )
    .test(
      'distribution',
      'As distribuições devem somar 100% ou serem iguais a 100% em todas EPSs',
      (value, testContext) => {
        const selectedEPSValues = Object.values(value);
        if (!selectedEPSValues.length) return true;

        const size = selectedEPSValues.length;
        const sum = selectedEPSValues.reduce(
          (sum, current) =>
            current.distributionPercentage
              ? sum + current.distributionPercentage
              : sum,
          0,
        );
        if (
          testContext?.parent?.serviceCategory === '6010a536b8dde377826544c1'
        ) {
          return true;
        }

        return (
          sum === 100 ||
          sum / size === 100 ||
          testContext.createError({ path: 'selectedEPS_Root' })
        );
      },
    ),
);

export const createMailingSchema = Yup.object().shape({
  _id: Yup.string().length(24).optional(),
  status: Yup.string().required(),
  serviceCategory: Yup.string().length(24).required(REQUIRED_MESSAGE),
  name: Yup.string().required(REQUIRED_MESSAGE),
  aggregationType: Yup.string().required(REQUIRED_MESSAGE),
  customerAging: Yup.object().when('aggregationType', {
    is: 'CUSTOMER',
    then: rangeFieldSchemaAllowMinZero,
    otherwise: Yup.object().optional(),
  }),
  shouldFilterOnContractAging: Yup.boolean(),
  contractAging: Yup.object()
    .when('shouldFilterOnContractAging', {
      is: true,
      then: rangeFieldSchemaAllowMinZero,
      otherwise: Yup.object().optional(),
    })
    .test(
      'minNotLowerThanCustomerAgingMin',
      'O atraso mínimo de contratos deve ser igual ou maior que o atraso mínimo de clientes',
      (value, testContext) => {
        if (
          testContext.parent.aggregationType === 'CONTRACT' ||
          !testContext.parent.shouldFilterOnContractAging
        ) {
          return true;
        }
        // if (value.min < testContext.parent.customerAging.min) {
        //   return testContext.createError({
        //     path: `${testContext.path}.min`,
        //   });
        // }
        return true;
      },
    )
    .test(
      'maxNotGreaterThanCustomerAgingMax',
      'O atraso máximo de contratos deve ser igual ou menor que o atraso máximo de clientes',
      (value, testContext) => {
        if (
          testContext.parent.aggregationType === 'CONTRACT' ||
          !testContext.parent.shouldFilterOnContractAging
        ) {
          return true;
        }
        if (value.max > testContext.parent.customerAging.max) {
          return testContext.createError({
            path: `${testContext.path}.max`,
            message:
              'O atraso máximo de contratos deve ser igual ou menor que o atraso máximo de clientes',
          });
        }
        return true;
      },
    ),
  customerType: Yup.array().required().min(1, MIN_MESSAGE).of(Yup.string()),
  corporateType: Yup.array().when('customerType', {
    is: (customerType: Array<string>) => customerType.includes('PJ'),
    then: Yup.array().required().min(1, MIN_MESSAGE).of(Yup.string()),
    otherwise: Yup.array().optional(),
  }),
  propensity: Yup.array()
    .required(REQUIRED_MESSAGE)
    .min(1, MIN_MESSAGE)
    .of(Yup.string()),
  systems: Yup.object()
    .required()
    .test('notAllArraysEmpty', MIN_MESSAGE, (value) =>
      Object.values(value).some((v: string[]) => !!v.length),
    ),
  billingType: Yup.array()
    .required(REQUIRED_MESSAGE)
    .min(1, MIN_MESSAGE)
    .of(Yup.string()),
  uf: Yup.array()
    .required(REQUIRED_MESSAGE)
    .min(1, MIN_MESSAGE)
    .of(Yup.string()),
  excludeExemption: Yup.boolean().optional(),
  activeOffer: Yup.array().optional().min(1, MIN_MESSAGE).of(Yup.string()),
  customerDebtsSum: Yup.object().when('aggregationType', {
    is: 'CUSTOMER',
    then: rangeFieldSchemaOptional,
    otherwise: Yup.object().optional(),
  }),
  contractDebtsSum: rangeFieldSchemaOptional,
  customerContractsSum: Yup.object().when('aggregationType', {
    is: 'CUSTOMER',
    then: rangeFieldSchemaOptional,
    otherwise: Yup.object().optional(),
  }),
  contractInvoicesSum: rangeFieldSchemaOptional,
  permanenceType: Yup.string().required(MIN_MESSAGE),
  permanenceInDays: Yup.number().when('permanenceType', {
    is: 'permanenceInDays',
    then: Yup.number()
      .required(REQUIRED_MESSAGE)
      .min(1, 'Deve ser maior que 0'),
    otherwise: Yup.number().optional(),
  }),
  permanenceUntilDate: Yup.date().when('permanenceType', {
    is: 'permanenceUntilDate',
    then: Yup.date()
      .required(REQUIRED_MESSAGE)
      .nullable()
      .min(
        new Date(addDays(new Date(), 1).setHours(0, 0, 0, 0)),
        'Data deve ser posterior a hoje',
      ),
    otherwise: Yup.date().optional(),
  }),
  paymentRemunerationDays: Yup.number()
    .required(REQUIRED_MESSAGE)
    .min(1, 'Deve ser maior que 0'),
  weekDaysToExecute: Yup.number().when('executionPeriodicityType', {
    is: 'weekly',
    then: Yup.number().min(0, MIN_MESSAGE),
    otherwise: Yup.number().optional(),
  }),
  debtsDistribution: Yup.string().required(MIN_MESSAGE),

  shouldIncludeNewInvoice: Yup.boolean().required(),
  invoiceAgingToInclude: Yup.object().when('shouldIncludeNewInvoice', {
    is: true,
    then: rangeFieldSchema,
    otherwise: Yup.object().optional(),
  }),
  periodUpdateDebt: Yup.number()
    .required(REQUIRED_MESSAGE)
    .moreThan(0, 'Deve ser maior que 0'),

  selectedEPS: selectedEPSSchema,
});
